import { IResortPathInfo, IWeatherInfo, ResortWeatherLocation } from 'api/resort/types';
import { Daily } from 'components/resort/ResortWeather/Daily';
import { Hourly } from 'components/resort/ResortWeather/Hourly';
import { useOTSTranslation } from 'i18n/useOTSTranslation';
import { useCallback, useState, ReactNode } from 'react';
import { useAnalytics } from 'util/analytics';
import { DistanceUnit } from 'components/blocks/Unit';
import { RegularAd } from 'googlead/components/RegularAd';
import { BoxWithShadow } from 'components/blocks/BoxWithShadow';
import { BlockHeader } from 'components/blocks/BlockHeader';
import styles from './styles.module.scss';

export const ResortWeather = (
  {
    weatherInfoDaily,
    weatherInfoHourly,
    aiAssistantWidget,
  }: {
    weatherInfoDaily: IWeatherInfo,
    weatherInfoHourly: IWeatherInfo,
    pathInfo: IResortPathInfo,
    aiAssistantWidget?: ReactNode,
  },
) => {
  const hasBase = Boolean(weatherInfoDaily.weatherItems && weatherInfoDaily.weatherItems.length > 0 && weatherInfoDaily.weatherItems[0].base);
  const hasSummit = Boolean(weatherInfoDaily.weatherItems && weatherInfoDaily.weatherItems.length > 0 && weatherInfoDaily.weatherItems[0].summit);
  const hasMid = Boolean(weatherInfoDaily.weatherItems && weatherInfoDaily.weatherItems.length > 0 && weatherInfoDaily.weatherItems[0].mid);

  const midElevation = weatherInfoDaily.mid;

  const activeTabDefault = () => {
    if (hasMid && hasBase && hasSummit) {
      return ResortWeatherLocation.middle;
    }

    if (hasBase) {
      return ResortWeatherLocation.base;
    }

    return ResortWeatherLocation.summit;
  };

  const [activeTab, setActiveTab] = useState(activeTabDefault);
  const { t } = useOTSTranslation('common');

  const a = useAnalytics();

  const setBase = useCallback(() => {
    /* a.viewContent({
      content: 'Hourly Weather Forecast',
    }); */
    setActiveTab(ResortWeatherLocation.base);
  }, [a, setActiveTab]);

  const setSummit = useCallback(() => {
    a.viewContent({
      content: 'Hourly Weather Forecast',
    });
    setActiveTab(ResortWeatherLocation.summit);
  }, [a, setActiveTab]);

  const setMiddle = useCallback(() => {
    setActiveTab(ResortWeatherLocation.middle);
  }, [a, setActiveTab]);

  return (
    <div className={styles.wrap}>
      <div className={styles.tabs}>
        {hasBase && (
          <>
            <button
              type="button"
              className={activeTab === ResortWeatherLocation.base ? styles.active : ''}
              onClick={setBase}
            >
              {t('common.base')}
              <span className={styles.elevation}>
                (
                <DistanceUnit values={weatherInfoDaily.base} type="mOrFeetShort" />
                )
              </span>
            </button>

          </>
        )}
        {hasMid && hasBase && hasSummit && (
          <>
            <button
              type="button"
              className={activeTab === ResortWeatherLocation.middle ? styles.active : ''}
              onClick={setMiddle}
            >
              {t('common.mid')}
              {midElevation && (
              <span className={styles.elevation}>
                (
                <DistanceUnit values={midElevation} type="mOrFeetShort" formatAll />
                )
              </span>
              )}
            </button>

          </>
        )}
        {hasSummit && (
          <>
            <button
              type="button"
              className={activeTab === ResortWeatherLocation.summit ? styles.active : ''}
              onClick={setSummit}
            >
              {t('common.summit')}
              <span className={styles.elevation}>
                (
                <DistanceUnit values={weatherInfoDaily.summit} type="mOrFeetShort" formatAll />
                )
              </span>
            </button>

          </>
        )}
      </div>
      <div>
        <Daily weatherDaily={weatherInfoDaily} weatherLocation={activeTab} />
        <RegularAd isFirst />
        {aiAssistantWidget}

        <BoxWithShadow>
          <BlockHeader title={t('resort.weather.hourly')} />
          <Hourly weatherHourly={weatherInfoHourly} weatherLocation={activeTab} />
        </BoxWithShadow>

      </div>
    </div>
  );
};
